import { useEffect, useRef, useState } from "react"
import Placeholder from "@tiptap/extension-placeholder"
import { EditorContent, useEditor } from "@tiptap/react"
import {
	IconEyeOutline18 as Eye,
	IconTriangleWarningOutline18 as WarningIcon,
} from "nucleo-ui-outline-18"

import type {
	SlackPreviewSegment,
	SlackWelcomeKind,
} from "@productlane/lib/src/slack-welcome"
import type { SlackConnectSettings } from "@productlane/zero"
import { cn } from "@productlane/lib/src/cn"
import {
	DEFAULT_SLACK_WELCOME_MESSAGES,
	previewSlackWelcomeMessage,
	slackWelcomeTemplate,
} from "@productlane/lib/src/slack-welcome"
import { plainTextExtensions } from "@productlane/lib/src/tiptap/plain-text-extensions"
import {
	Avatar,
	Button,
	Dialog,
	IconButton,
	Switch,
	Tooltip,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { SlackWelcomeVariableSuggestion } from "@/components/editor/extensions/slack-welcome-variables"
import { toast } from "@/components/toast"
import { env } from "@/env"
import { analytics } from "@/lib/analytics"
import { useZero, useZeroWorkspace } from "@/lib/zero"
import { templateTextToDoc } from "../settings-pages/close-loop-template-editor"
import { SettingItem } from "../settings-switches/setting-item"

const SAVE_DEBOUNCE_MS = 700

interface SlackTeamIdentity {
	name: string
	iconUrl: string | null
}

const PREVIEW_PERSON = { name: "John Doe", firstName: "John", initials: "JD" }
const PREVIEW_CHANNEL = "shared-support"

const PLACEHOLDERS: Record<SlackWelcomeKind, string> = {
	channel: "Type { to add {name} or {channel}",
	community: "Type { to add {name}",
}

// The default copy is stored as null, so a later improvement to it reaches everyone who kept it.
export const storedWelcomeMessage = (kind: SlackWelcomeKind, text: string) =>
	!text.trim() || text.trim() === DEFAULT_SLACK_WELCOME_MESSAGES[kind]
		? null
		: text

export function SlackWelcomeMessages({
	canSendDms,
	slackTeam,
	settings,
}: {
	canSendDms: boolean | null | undefined
	slackTeam: SlackTeamIdentity | null | undefined
	/** The Slack workspace whose welcome messages these are. */
	settings: SlackConnectSettings
}) {
	const workspace = useZeroWorkspace()
	const zero = useZero()
	const [previewKind, setPreviewKind] = useState<SlackWelcomeKind | null>(null)

	if (!workspace) return null

	const save = async (
		updates: Partial<{
			channelWelcomeEnabled: boolean
			channelWelcomeMessage: string | null
			communityWelcomeEnabled: boolean
			communityWelcomeMessage: string | null
		}>,
	) => {
		const result = await zero.mutate(
			mutators.slackConnectSettings.update({ id: settings.id, ...updates }),
		).client
		if (result.type === "error") {
			toast.error({
				title: "Your welcome message didn't save",
				description: "Try again in a moment.",
			})
		}
	}

	const toggle = (kind: SlackWelcomeKind, enabled: boolean) => {
		analytics.track("update-slack-settings")
		void save(
			kind === "channel"
				? { channelWelcomeEnabled: enabled }
				: { communityWelcomeEnabled: enabled },
		)
	}

	const saveMessage = (kind: SlackWelcomeKind, text: string) => {
		const stored = storedWelcomeMessage(kind, text)
		const current =
			kind === "channel"
				? settings.channelWelcomeMessage
				: settings.communityWelcomeMessage
		if ((current ?? null) === stored) return
		void save(
			kind === "channel"
				? { channelWelcomeMessage: stored }
				: { communityWelcomeMessage: stored },
		)
	}

	const reconnect = () => {
		analytics.track("connect-slack")
		window.location.href = `${env.VITE_API_URL}/api/auth/slack?state=slack-connect`
	}

	// Rows of the workspace's card, so the card's dividers run between them.
	return (
		<>
			<SettingItem
				className="pt-4"
				title="Channel welcome"
				description="Greets customers who join a shared channel."
				action={
					<Switch
						name="channelWelcomeEnabled"
						checked={settings.channelWelcomeEnabled}
						onCheckedChange={(checked) => toggle("channel", checked)}
					/>
				}
			/>
			{settings.channelWelcomeEnabled && (
				<div className="!border-t-0">
					<WelcomeMessageEditor
						kind="channel"
						value={slackWelcomeTemplate(
							"channel",
							settings.channelWelcomeMessage,
						)}
						onSave={(text) => saveMessage("channel", text)}
						onPreview={() => setPreviewKind("channel")}
					/>
				</div>
			)}
			<SettingItem
				className="pt-4"
				title="Community welcome"
				description="DMs everyone who joins your Slack workspace."
				action={
					<Switch
						name="communityWelcomeEnabled"
						checked={settings.communityWelcomeEnabled}
						onCheckedChange={(checked) => toggle("community", checked)}
					/>
				}
			/>
			{settings.communityWelcomeEnabled && (
				<div className="space-y-2 !border-t-0">
					{canSendDms === false && (
						<div className="flex items-center justify-between gap-3 rounded-lg bg-amber-50 py-2 pl-3 pr-2 text-xs text-amber-700 ring-1 ring-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:ring-amber-900/60">
							<span className="flex items-center gap-2">
								<WarningIcon className="h-4 w-4 flex-none" />
								Reconnect Slack to send DMs.
							</span>
							<Button size="sm" onClick={reconnect}>
								Reconnect
							</Button>
						</div>
					)}
					<WelcomeMessageEditor
						kind="community"
						value={slackWelcomeTemplate(
							"community",
							settings.communityWelcomeMessage,
						)}
						onSave={(text) => saveMessage("community", text)}
						onPreview={() => setPreviewKind("community")}
					/>
				</div>
			)}

			<SlackWelcomePreviewDialog
				kind={previewKind}
				message={
					previewKind === "community"
						? slackWelcomeTemplate(
								"community",
								settings.communityWelcomeMessage,
							)
						: slackWelcomeTemplate("channel", settings.channelWelcomeMessage)
				}
				workspaceName={workspace.name}
				workspaceLogoUrl={workspace.logoUrl ?? null}
				slackTeam={slackTeam ?? null}
				onClose={() => setPreviewKind(null)}
			/>
		</>
	)
}

function WelcomeMessageEditor({
	kind,
	value,
	onSave,
	onPreview,
}: {
	kind: SlackWelcomeKind
	value: string
	onSave: (text: string) => void
	onPreview: () => void
}) {
	const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
	const onSaveRef = useRef(onSave)
	onSaveRef.current = onSave

	const editor = useEditor({
		extensions: [
			...plainTextExtensions(),
			Placeholder.configure({ placeholder: PLACEHOLDERS[kind] }),
			SlackWelcomeVariableSuggestion.configure({ kind }),
		],
		content: templateTextToDoc(value),
		editorProps: {
			attributes: { "aria-label": `${kindLabel(kind)} message` },
		},
		onUpdate: ({ editor }) => {
			clearTimeout(saveTimer.current)
			const text = editor.getText({ blockSeparator: "\n" })
			saveTimer.current = setTimeout(() => {
				saveTimer.current = undefined
				onSaveRef.current(text)
			}, SAVE_DEBOUNCE_MS)
		},
		onBlur: ({ editor }) => {
			clearTimeout(saveTimer.current)
			saveTimer.current = undefined
			const text = editor.getText({ blockSeparator: "\n" })
			// An empty box would still send the default, so it shows the default again.
			if (!text.trim()) {
				editor.commands.setContent(
					templateTextToDoc(DEFAULT_SLACK_WELCOME_MESSAGES[kind]),
					false,
				)
			}
			onSaveRef.current(text)
		},
	})

	// Flush a pending save when the section closes mid-edit.
	useEffect(
		() => () => {
			if (!saveTimer.current || !editor) return
			clearTimeout(saveTimer.current)
			onSaveRef.current(editor.getText({ blockSeparator: "\n" }))
		},
		[editor],
	)

	// Settings hydrate after mount; only an idle editor takes the stored text.
	useEffect(() => {
		if (!editor || editor.isFocused) return
		if (editor.getText({ blockSeparator: "\n" }) === value) return
		editor.commands.setContent(templateTextToDoc(value), false)
	}, [editor, value])

	return (
		<div className="relative cursor-text rounded-lg bg-white py-2.5 pl-3 pr-10 text-[13px] leading-5 ring-1 ring-gray-200 focus-within:ring-gray-400 dark:bg-transparent dark:ring-gray-700/80 dark:focus-within:ring-gray-500 [&_.ProseMirror]:outline-none [&_.ProseMirror_p]:m-0 [&_.ProseMirror_p]:min-h-5">
			<EditorContent editor={editor} />
			<div className="absolute right-1.5 top-1.5">
				<Tooltip label="Preview in Slack">
					<IconButton
						variant="ghost"
						Icon={Eye}
						aria-label="Preview in Slack"
						className="cursor-default"
						onClick={onPreview}
					/>
				</Tooltip>
			</div>
		</div>
	)
}

const kindLabel = (kind: SlackWelcomeKind) =>
	kind === "channel" ? "Channel welcome" : "Community welcome"

function SlackWelcomePreviewDialog({
	kind,
	message,
	workspaceName,
	workspaceLogoUrl,
	slackTeam,
	onClose,
}: {
	kind: SlackWelcomeKind | null
	message: string
	workspaceName: string
	workspaceLogoUrl: string | null
	slackTeam: SlackTeamIdentity | null
	onClose: () => void
}) {
	const isChannel = kind !== "community"
	const teamName = slackTeam?.name ?? workspaceName
	const lines = previewSlackWelcomeMessage({
		template: message,
		name: PREVIEW_PERSON.firstName,
		channelName: PREVIEW_CHANNEL,
	})

	return (
		<Dialog
			isOpen={kind !== null}
			onOpenChange={(open) => !open && onClose()}
			size="xl"
			title="Preview in Slack"
			description={
				isChannel
					? `What ${PREVIEW_PERSON.name} sees after joining #${PREVIEW_CHANNEL}. Only they see it.`
					: `What ${PREVIEW_PERSON.name} gets after joining your Slack workspace.`
			}
		>
			<div className="overflow-hidden rounded-xl font-[Lato,system-ui,sans-serif] shadow-[0_1px_2px_rgba(0,0,0,0.06),0_2px_6px_-2px_rgba(0,0,0,0.08)] ring-1 ring-black/20 dark:shadow-none dark:ring-white/15">
				<div className="flex h-10 items-center border-b border-[#DDDDDD] bg-[#F1F1F1] px-3.5 dark:border-[#2C2D30] dark:bg-[#121016]">
					<div aria-hidden className="flex gap-2">
						<span className="h-3 w-3 rounded-full bg-[#FF5F57]" />
						<span className="h-3 w-3 rounded-full bg-[#FEBC2E]" />
						<span className="h-3 w-3 rounded-full bg-[#28C840]" />
					</div>
				</div>

				<div className="flex min-h-[420px] bg-[#1A1D21] dark:bg-[#121016]">
					<div className="hidden w-52 flex-none flex-col gap-4 px-2.5 py-3.5 text-[#D1D2D3] sm:flex">
						<div className="flex items-center gap-2 px-2">
							<Avatar
								name={teamName}
								image={slackTeam?.iconUrl ?? workspaceLogoUrl}
								className="h-6 w-6 flex-none rounded-md"
							/>
							<span className="truncate text-[15px] font-black text-white">
								{teamName}
							</span>
						</div>
						<div className="flex flex-col gap-0.5 text-sm">
							<div className="px-2 py-1 text-xs text-[#ABABAD]">Channels</div>
							{(isChannel
								? ["general", PREVIEW_CHANNEL, "random"]
								: ["announcements", "general", "help"]
							).map((channel) => (
								<div
									key={channel}
									className={cn(
										"truncate rounded-md px-2 py-1",
										isChannel &&
											channel === PREVIEW_CHANNEL &&
											"bg-[#1164A3] font-bold text-white",
									)}
								>
									# {channel}
								</div>
							))}
							{!isChannel && (
								<>
									<div className="px-2 pb-1 pt-3 text-xs text-[#ABABAD]">
										Apps
									</div>
									<div className="truncate rounded-md bg-[#1164A3] px-2 py-1 font-bold text-white">
										{workspaceName}
									</div>
								</>
							)}
						</div>
					</div>

					<div className="flex min-w-0 flex-1 flex-col bg-white text-[#1D1C1D] dark:bg-[#1A1D21] dark:text-[#D1D2D3]">
						<div className="flex items-center gap-2 border-b border-[#E8E8E8] px-5 py-3 dark:border-[#35373B]">
							<span className="truncate text-[17px] font-black">
								{isChannel ? `# ${PREVIEW_CHANNEL}` : workspaceName}
							</span>
							{!isChannel && <SlackAppTag />}
						</div>

						<div className="flex flex-1 flex-col justify-end gap-4 px-5 py-4">
							{isChannel ? (
								<div className="flex items-center gap-2.5">
									<span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-[#E4DDF4] text-[13px] font-semibold text-[#3E2A78]">
										{PREVIEW_PERSON.initials}
									</span>
									<span className="text-[15px] text-[#616061] dark:text-[#ABABAD]">
										<span className="font-black text-[#1D1C1D] dark:text-[#D1D2D3]">
											{PREVIEW_PERSON.name}
										</span>{" "}
										joined #{PREVIEW_CHANNEL}.
									</span>
								</div>
							) : (
								<div className="flex items-center gap-3 text-[13px] text-[#616061] dark:text-[#ABABAD]">
									<span className="h-px flex-1 bg-[#E8E8E8] dark:bg-[#35373B]" />
									Today
									<span className="h-px flex-1 bg-[#E8E8E8] dark:bg-[#35373B]" />
								</div>
							)}

							<div
								className={cn(
									"-mx-2.5 rounded-lg px-2.5 py-2",
									isChannel && "bg-[#F8F8F8] dark:bg-[#222529]",
								)}
							>
								{isChannel && (
									<div className="mb-1.5 flex items-center gap-1.5 pl-[46px] text-xs text-[#616061] dark:text-[#ABABAD]">
										<Eye className="h-3.5 w-3.5 flex-none" />
										Only visible to you
									</div>
								)}
								<div className="flex gap-2.5">
									<Avatar
										name={workspaceName}
										image={workspaceLogoUrl}
										className="h-9 w-9 flex-none rounded-lg"
									/>
									<div className="flex min-w-0 flex-col gap-0.5">
										<div className="flex items-baseline gap-1.5">
											<span className="text-[15px] font-black">
												{workspaceName}
											</span>
											<SlackAppTag />
											<span className="text-xs text-[#616061] dark:text-[#ABABAD]">
												10:42 AM
											</span>
										</div>
										{lines.map((segments, index) => (
											<div
												key={index}
												className="min-h-[22px] whitespace-pre-wrap break-words text-[15px] leading-[22px]"
											>
												{segments.map((segment, i) => (
													<SlackSegment key={i} segment={segment} />
												))}
											</div>
										))}
									</div>
								</div>
							</div>
						</div>

						<div className="px-5 pb-5">
							<div className="rounded-lg border border-[#DDDDDD] px-3 py-3 text-[15px] text-[#868686] dark:border-[#565856] dark:text-[#ABABAD]">
								Message {isChannel ? `#${PREVIEW_CHANNEL}` : workspaceName}
							</div>
						</div>
					</div>
				</div>
			</div>
		</Dialog>
	)
}

function SlackAppTag() {
	return (
		<span className="flex-none rounded-sm bg-[#EBEAEB] px-1 text-[10px] font-bold text-[#616061] dark:bg-[#35373B] dark:text-[#ABABAD]">
			APP
		</span>
	)
}

function SlackSegment({ segment }: { segment: SlackPreviewSegment }) {
	if (segment.code)
		return (
			<code className="rounded border border-[#DDDDDD] bg-[#F8F8F8] px-1 font-mono text-xs text-[#E01E5A] dark:border-[#3A3D42] dark:bg-[#222529] dark:text-[#E8912D]">
				{segment.text}
			</code>
		)
	return (
		<span
			className={cn(
				segment.bold && "font-black",
				segment.italic && "italic",
				segment.strike && "line-through",
				(segment.link || segment.mention) &&
					"text-[#1264A3] dark:text-[#1D9BD1]",
				segment.mention &&
					"rounded-sm bg-[#E8F5FA] px-0.5 dark:bg-[#1D9BD1]/10",
			)}
		>
			{segment.text}
		</span>
	)
}
